// =================================================================================
// File: src/pages/TemplatesPage.jsx
// Description: Design templates library with category management for Canva designs
// =================================================================================

import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Plus, Trash2, Download, FolderOpen, Image, FileText, Share2, Palette } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useStorage } from "../contexts/StorageContext";

const CATEGORY_ICONS = {
  folder: FolderOpen,
  file: FileText,
  image: Image,
  share: Share2,
  palette: Palette,
};

const CATEGORY_STORAGE_KEY = "mudoTemplateCategories";

const DEFAULT_TEMPLATE_CATEGORIES = [
  { id: "all", label: "All Templates", icon: "folder", color: "#9333ea" },
  { id: "workshops", label: "Workshop Posters", icon: "file", color: "#3b82f6" },
  { id: "products", label: "Product Cards", icon: "image", color: "#10b981" },
  { id: "socials", label: "Social Media", icon: "share", color: "#f59e0b" },
  { id: "branding", label: "Studio Branding", icon: "palette", color: "#ec4899" },
  { id: "other", label: "Other", icon: "folder", color: "#6b7280" },
];

const normalizeCategories = (storedCategories) => {
  if (!Array.isArray(storedCategories) || storedCategories.length === 0) {
    return DEFAULT_TEMPLATE_CATEGORIES;
  }

  const sanitized = storedCategories
    .filter((cat) => cat && typeof cat.id === "string")
    .map((cat) => ({
      ...cat,
      icon: typeof cat.icon === "string" ? cat.icon : "folder",
      color: cat.color || "#6b7280",
    }));

  const defaultIds = DEFAULT_TEMPLATE_CATEGORIES.map((cat) => cat.id);

  const mergedDefaults = DEFAULT_TEMPLATE_CATEGORIES.map((defaultCat) => {
    const stored = sanitized.find((cat) => cat.id === defaultCat.id);
    if (!stored) return defaultCat;
    return {
      ...defaultCat,
      ...stored,
      icon: stored.icon || defaultCat.icon,
      color: stored.color || defaultCat.color,
    };
  });

  const extras = sanitized.filter((cat) => !defaultIds.includes(cat.id));

  return [...mergedDefaults, ...extras];
};

/**
 * TemplatesPage - Manage design templates from Canva
 * 
 * Features:
 * - Category-based organization
 * - Upload/manage templates
 * - Filter by category
 * - Delete templates
 * - View template details
 */
export default function TemplatesPage() {
  const navigate = useNavigate();
  const { hasFolder, isDriveLinked, isDriveAuthed, readJSON, writeJSON } = useStorage();
  const hasWorkspace = !!hasFolder || (!!isDriveLinked && !!isDriveAuthed);
  const loadedRef = useRef(false);
  
  const [templates, setTemplates] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [categories, setCategories] = useState(DEFAULT_TEMPLATE_CATEGORIES);
  const [viewerTemplate, setViewerTemplate] = useState(null);
  const userMutatedRef = useRef(false);

  const updateCategories = useCallback((updater) => {
    setCategories((prev) => {
      const next = typeof updater === "function" ? updater(prev) : updater;
      try {
        localStorage.setItem(CATEGORY_STORAGE_KEY, JSON.stringify(next));
      } catch (error) {
        console.error("Error saving categories:", error);
      }
      return next;
    });
  }, []);

  // Load templates from workspace (folder or drive)
  useEffect(() => {
    let alive = true;
    loadedRef.current = false;

    (async () => {
      if (!hasWorkspace) {
        if (alive) setTemplates([]);
        loadedRef.current = true;
        return;
      }
      try {
        const disk = await readJSON("widgets/templates.json", []);
        if (!alive) return;
        if (!userMutatedRef.current) {
          setTemplates(Array.isArray(disk) ? disk : []);
        }
      } catch {
        if (!alive) return;
        if (!userMutatedRef.current) {
          setTemplates([]);
        }
      } finally {
        loadedRef.current = true;
      }
    })();

    return () => { alive = false; };
  }, [hasWorkspace, readJSON]);

  // Rehydrate after restore events and clear on workspace disconnect
  useEffect(() => {
    const onRestored = async () => {
      if (!hasWorkspace) return;
      try {
        const disk = await readJSON("widgets/templates.json", []);
        setTemplates(Array.isArray(disk) ? disk : []);
      } catch {}
    };
    
    const onDisconnect = () => {
      // CRITICAL: Clear all data when workspace disconnected
      setTemplates([]);
      loadedRef.current = false;
    };
    
    window.addEventListener("mudo:vault-restored", onRestored);
    window.addEventListener("mudo:files-restored", onRestored);
    window.addEventListener("mudo:workspace-disconnected", onDisconnect);
    return () => {
      window.removeEventListener("mudo:vault-restored", onRestored);
      window.removeEventListener("mudo:files-restored", onRestored);
      window.removeEventListener("mudo:workspace-disconnected", onDisconnect);
    };
  }, [readJSON, hasWorkspace]);

  // Persist templates to workspace when they change
  useEffect(() => {
    if (!loadedRef.current || !hasWorkspace) return;
    (async () => {
      try {
        await writeJSON("widgets/templates.json", templates);
        window.dispatchEvent(new Event("mudo:templates-updated"));
      } catch (error) {
        console.error("Error saving templates:", error);
      }
    })();
  }, [templates, hasWorkspace, writeJSON]);

  useEffect(() => {
    try {
      const stored = localStorage.getItem(CATEGORY_STORAGE_KEY);
      if (!stored) return;
      const parsed = JSON.parse(stored);
      if (Array.isArray(parsed)) {
        setCategories(normalizeCategories(parsed));
      }
    } catch (error) {
      console.error("Error loading categories:", error);
      setCategories(DEFAULT_TEMPLATE_CATEGORIES);
    }
  }, []);

  // Filter templates by category
  const filteredTemplates = templates.filter(
    (t) => selectedCategory === "all" || t.category === selectedCategory
  );

  // Delete template
  const handleDelete = (templateId) => {
    if (!confirm("Delete this template?")) return;
    userMutatedRef.current = true;
    setTemplates((prev) => prev.filter((t) => t.id !== templateId));
  };

  // Add new template
  const handleAddTemplate = (newTemplate) => {
    const template = {
      id: Date.now().toString(),
      name: newTemplate.name,
      category: newTemplate.category,
      imageUrl: newTemplate.imageUrl || null,
      fileUrl: newTemplate.fileUrl || null,
      createdAt: new Date().toISOString(),
      source: "manual",
    };
    
    userMutatedRef.current = true;
    setTemplates((prev) => [...prev, template]);
    setUploadModalOpen(false);
  };

  const generateCategoryId = (label) => {
    const base = label
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "");
    const fallback = base || "category";
    let candidate = fallback;
    let suffix = 1;

    while (categories.some((cat) => cat.id === candidate)) {
      candidate = `${fallback}-${suffix++}`;
    }

    return candidate;
  };

  const handleCreateCategory = (label) => {
    const trimmed = label.trim();
    if (!trimmed) return null;

    const newCategory = {
      id: generateCategoryId(trimmed),
      label: trimmed,
      icon: "folder",
      color: "#6b7280",
    };

    updateCategories((prev) => [...prev, newCategory]);
    return newCategory;
  };

  const handleUpdateCategory = (categoryId, nextLabel) => {
    const trimmed = nextLabel.trim();
    if (!trimmed) return;

    updateCategories((prev) =>
      prev.map((cat) => (cat.id === categoryId ? { ...cat, label: trimmed } : cat))
    );
  };

  return (
    <div className="min-h-screen" style={{ background: "#0f0f10", color: "#fff" }}>
      {/* Header */}
      <div
        style={{
          position: "sticky",
          top: 0,
          zIndex: 100,
          background: "rgba(18,18,18,0.7)",
          backdropFilter: "blur(12px)",
          WebkitBackdropFilter: "blur(12px)",
          borderBottom: "1px solid rgba(255,255,255,0.08)",
        }}
      >
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <button
            onClick={() => navigate("/dashboard")}
            className="flex items-center gap-2 px-4 py-2 rounded-full mudo-glass-pill mudo-glass-interactive"
          >
            <ArrowLeft className="w-5 h-5" />
            <span className="font-semibold">Back</span>
          </button>
          
          <h1 className="text-2xl font-bold">Design Templates</h1>
          
          <button
            onClick={() => setUploadModalOpen(true)}
            className="flex items-center gap-2 px-4 py-2 rounded-full mudo-glass-interactive"
            style={{ 
              background: "var(--color-primary)",
              backdropFilter: "blur(16px)", 
              WebkitBackdropFilter: "blur(16px)",
              border: "1px solid rgba(255,255,255,0.2)"
            }}
          >
            <Plus className="w-5 h-5" />
            <span className="font-semibold">Add Template</span>
          </button>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Category Filters */}
        <div className="flex items-center gap-3 mb-8 overflow-x-auto pb-4">
          {categories.map((cat) => {
            const Icon = CATEGORY_ICONS[cat.icon] || FolderOpen;
            const isActive = selectedCategory === cat.id;
            
            return (
              <button
                key={cat.id}
                onClick={() => setSelectedCategory(cat.id)}
                className="flex items-center gap-2 px-4 py-2 rounded-full whitespace-nowrap mudo-glass-pill mudo-glass-interactive"
                style={{
                  background: isActive ? `${cat.color}20` : "rgba(255,255,255,0.05)",
                  border: `1px solid ${isActive ? cat.color : "rgba(255,255,255,0.1)"}`,
                  color: isActive ? cat.color : "#fff",
                }}
              >
                <Icon className="w-4 h-4" />
                <span className="font-medium text-sm">{cat.label}</span>
                <span className="text-xs opacity-60">
                  ({cat.id === "all" ? templates.length : templates.filter(t => t.category === cat.id).length})
                </span>
              </button>
            );
          })}
        </div>

        {/* Templates Grid */}
        {filteredTemplates.length > 0 ? (
          <motion.div
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6"
            initial="hidden"
            animate="visible"
            variants={{
              hidden: {},
              visible: {
                transition: {
                  staggerChildren: 0.05,
                },
              },
            }}
          >
            <AnimatePresence>
              {filteredTemplates.map((template) => (
                <TemplateCard
                  key={template.id}
                  template={template}
                  onDelete={handleDelete}
                  categories={categories}
                  onOpenViewer={(tpl) => setViewerTemplate(tpl)}
                />
              ))}
            </AnimatePresence>
          </motion.div>
        ) : (
          <EmptyState
            category={selectedCategory}
            categories={categories}
            onAddTemplate={() => setUploadModalOpen(true)}
          />
        )}
      </div>

      {/* Upload Modal */}
      {uploadModalOpen && (
        <UploadModal
          onClose={() => setUploadModalOpen(false)}
          onSave={handleAddTemplate}
          categories={categories}
          onCategoryCreate={handleCreateCategory}
          onCategoryUpdate={handleUpdateCategory}
        />
      )}

      {viewerTemplate && (
        <TemplateViewerModal
          template={viewerTemplate}
          onClose={() => setViewerTemplate(null)}
        />
      )}
    </div>
  );
}

/**
 * TemplateViewerModal - Fullscreen preview for templates (image/PDF)
 */
function TemplateViewerModal({ template, onClose }) {
  const [objectUrl, setObjectUrl] = useState(null);
  const isPdf = useMemo(() => {
    const url = template?.fileUrl || template?.imageUrl || "";
    return (
      url.includes("application/pdf") ||
      (typeof url === "string" && url.toLowerCase().endsWith(".pdf"))
    );
  }, [template]);

  useEffect(() => {
    // Convert base64 data URL to blob URL for PDFs for better browser viewer support
    const url = template?.fileUrl || template?.imageUrl || null;
    if (!url) return;

    if (url.startsWith("data:")) {
      try {
        const [header, data] = url.split(",");
        const mime = header.substring(5, header.indexOf(";"));
        const binary = atob(data);
        const len = binary.length;
        const buffer = new Uint8Array(len);
        for (let i = 0; i < len; i++) buffer[i] = binary.charCodeAt(i);
        const blob = new Blob([buffer], { type: mime });
        const blobUrl = URL.createObjectURL(blob);
        setObjectUrl(blobUrl);
        return () => URL.revokeObjectURL(blobUrl);
      } catch (e) {
        console.warn("Failed to create blob URL for preview", e);
        setObjectUrl(null);
      }
    } else {
      setObjectUrl(url);
    }
  }, [template]);

  const download = () => {
    const source = template?.fileUrl || template?.imageUrl;
    if (!source) return;
    const link = document.createElement("a");
    link.href = objectUrl || source;
    const base = (template?.name || "template").replace(/[^a-z0-9]+/gi, "_").toLowerCase();
    const ext = isPdf ? "pdf" : "png";
    link.download = `${base}.${ext}`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div
      className="fixed inset-0 z-[10000] flex flex-col"
      style={{ background: "rgba(0,0,0,0.9)" }}
      onClick={onClose}
    >
      {/* Top bar */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-white/10" style={{ background: "rgba(18,18,18,0.95)", backdropFilter: "blur(20px)", WebkitBackdropFilter: "blur(20px)" }} onClick={(e) => e.stopPropagation()}>
        <div className="text-sm opacity-80 truncate">{template?.name}</div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => window.open(objectUrl || template?.fileUrl || template?.imageUrl, "_blank")}
            className="px-3 py-1.5 rounded bg-white/10 hover:bg-white/20 text-sm mudo-glass-pill mudo-glass-interactive"
          >
            Open in New Tab
          </button>
          <button onClick={download} className="px-3 py-1.5 rounded bg-white/10 hover:bg-white/20 text-sm mudo-glass-pill mudo-glass-interactive">Download</button>
          <button onClick={onClose} className="px-3 py-1.5 rounded bg-white/10 hover:bg-white/20 text-sm mudo-glass-pill mudo-glass-interactive">Close</button>
        </div>
      </div>

      {/* Viewer */}
      <div className="flex-1 overflow-hidden" onClick={(e) => e.stopPropagation()}>
        {isPdf ? (
          <iframe
            title="Template PDF Preview"
            src={(objectUrl || "") + "#zoom=page-fit"}
            className="w-full h-full"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center p-6">
            <img
              src={objectUrl || template?.imageUrl}
              alt={template?.name}
              className="max-w-full max-h-full object-contain rounded"
            />
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * TemplateCard - Individual template card
 */
function TemplateCard({ template, onDelete, categories = DEFAULT_TEMPLATE_CATEGORIES, onOpenViewer }) {
  const [showExportMenu, setShowExportMenu] = useState(false);
  const categoryInfo =
    categories.find((c) => c.id === template.category) ||
    categories[0] ||
    DEFAULT_TEMPLATE_CATEGORIES[0];
  const Icon = CATEGORY_ICONS[categoryInfo?.icon] || FolderOpen;

  // Export template in different formats
  const handleExport = (format) => {
    if (!template.imageUrl) {
      alert("No image available to export");
      return;
    }

    // For base64 images, convert and download
    if (template.imageUrl.startsWith("data:")) {
      const link = document.createElement("a");
      link.href = template.imageUrl;
      
      // Determine file extension from base64 or use format
      let extension = format.toLowerCase();
      if (template.imageUrl.includes("data:image/png")) {
        extension = "png";
      } else if (template.imageUrl.includes("data:image/jpeg") || template.imageUrl.includes("data:image/jpg")) {
        extension = "jpg";
      } else if (template.imageUrl.includes("data:application/pdf")) {
        extension = "pdf";
      }
      
      link.download = `${template.name.replace(/[^a-z0-9]/gi, '_').toLowerCase()}.${extension}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setShowExportMenu(false);
      return;
    }

    // For URLs, open in new tab
    window.open(template.imageUrl, "_blank");
    setShowExportMenu(false);
  };

  return (
    <motion.div
      variants={{
        hidden: { opacity: 0, y: 20 },
        visible: { opacity: 1, y: 0 },
      }}
      exit={{ opacity: 0, scale: 0.95 }}
      whileHover={{ y: -4 }}
      className="rounded-2xl overflow-hidden mudo-glass-panel mudo-glass-interactive"
      style={{
        background: "rgba(255,255,255,0.05)",
        border: "1px solid rgba(255,255,255,0.1)",
        transition: "all 0.2s",
      }}
    >
      {/* Image/Thumbnail (click to open viewer) */}
      <div
        className="w-full h-64 md:h-72 relative"
        style={{
          background: `linear-gradient(135deg, ${categoryInfo.color}40 0%, ${categoryInfo.color}20 100%)`,
        }}
        onClick={() => onOpenViewer && onOpenViewer(template)}
      >
        {template.imageUrl ? (
          <img
            src={template.imageUrl}
            alt={template.name}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Icon className="w-16 h-16" style={{ color: categoryInfo.color, opacity: 0.3 }} />
          </div>
        )}
        
        {/* Category Badge */}
        <div
          className="absolute top-3 left-3 px-3 py-1 rounded-full text-xs font-semibold"
          style={{
            background: `${categoryInfo.color}90`,
            backdropFilter: "blur(10px)",
            WebkitBackdropFilter: "blur(10px)",
          }}
        >
          {categoryInfo.label}
        </div>

        {/* Action Buttons */}
        <div className="absolute top-3 right-3 flex gap-2">
          {/* Export Button */}
          <div className="relative" onClick={(e) => e.stopPropagation()}>
            <button
              onClick={() => setShowExportMenu(!showExportMenu)}
              className="p-2 rounded-full bg-green-600/80 hover:bg-green-600 backdrop-blur-sm transition-all"
              title="Export template"
            >
              <Download className="w-4 h-4" />
            </button>
            
            {/* Export Menu */}
            {showExportMenu && (
              <div
                className="absolute top-full right-0 mt-2 w-32 rounded-lg overflow-hidden shadow-xl z-10"
                style={{
                  background: "rgba(18,18,18,0.95)",
                  border: "1px solid rgba(255,255,255,0.1)",
                  backdropFilter: "blur(10px)",
                }}
              >
                <button
                  onClick={() => handleExport("png")}
                  className="w-full px-4 py-2 text-left text-sm hover:bg-white/10 transition-colors"
                >
                  Export as PNG
                </button>
                <button
                  onClick={() => handleExport("jpg")}
                  className="w-full px-4 py-2 text-left text-sm hover:bg-white/10 transition-colors"
                >
                  Export as JPG
                </button>
                <button
                  onClick={() => handleExport("pdf")}
                  className="w-full px-4 py-2 text-left text-sm hover:bg-white/10 transition-colors"
                >
                  Export as PDF
                </button>
              </div>
            )}
          </div>

          {/* Delete Button */}
          <button
            onClick={(e) => { e.stopPropagation(); onDelete(template.id); }}
            className="p-2 rounded-full bg-red-600/80 hover:bg-red-600 backdrop-blur-sm transition-all"
            title="Delete template"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="p-4" onClick={() => onOpenViewer && onOpenViewer(template)}>
        <h3 className="font-semibold text-lg mb-2 truncate">{template.name}</h3>
        <div className="flex items-center justify-between text-xs text-white/60">
          <span>
            {new Date(template.createdAt).toLocaleDateString()}
          </span>
          <span className="capitalize">{template.source || "canva"}</span>
        </div>
      </div>
    </motion.div>
  );
}

/**
 * EmptyState - Shown when no templates in category
 */
function EmptyState({ category, onAddTemplate, categories = DEFAULT_TEMPLATE_CATEGORIES }) {
  const categoryInfo =
    categories.find((c) => c.id === category) ||
    categories.find((c) => c.id === "all") ||
    DEFAULT_TEMPLATE_CATEGORIES[0];
  const Icon = CATEGORY_ICONS[categoryInfo?.icon] || FolderOpen;

  return (
    <div className="text-center py-20">
      <Icon
        className="w-20 h-20 mx-auto mb-6"
        style={{ color: categoryInfo.color, opacity: 0.3 }}
      />
      <h3 className="text-2xl font-bold mb-3">
        No {category === "all" ? "" : categoryInfo.label.toLowerCase()} templates yet
      </h3>
      <p className="text-white/60 mb-8 max-w-md mx-auto">
        {category === "all"
          ? "Create stunning designs in Canva and save them here for easy access."
          : `Add templates to the ${categoryInfo.label} category.`}
      </p>
      <button
        onClick={onAddTemplate}
        className="px-6 py-3 rounded-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500 transition-all font-semibold"
      >
        Add Your First Template
      </button>
    </div>
  );
}

/**
 * UploadModal - Add template manually
 */
function UploadModal({
  onClose,
  onSave,
  categories = DEFAULT_TEMPLATE_CATEGORIES,
  onCategoryCreate,
  onCategoryUpdate,
}) {
  const selectableCategories = useMemo(
    () => (categories || DEFAULT_TEMPLATE_CATEGORIES).filter((cat) => cat.id !== "all"),
    [categories]
  );
  const [name, setName] = useState("");
  const [category, setCategory] = useState(
    () => selectableCategories[0]?.id || "other"
  );
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [manageCategories, setManageCategories] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState("");
  const [categoryEdits, setCategoryEdits] = useState(() => {
    const edits = {};
    selectableCategories.forEach((cat) => {
      edits[cat.id] = cat.label;
    });
    return edits;
  });

  useEffect(() => {
    setCategoryEdits((prev) => {
      const edits = {};
      selectableCategories.forEach((cat) => {
        edits[cat.id] = prev[cat.id] ?? cat.label;
      });
      return edits;
    });
  }, [selectableCategories]);

  useEffect(() => {
    if (!selectableCategories.some((cat) => cat.id === category) && selectableCategories.length > 0) {
      setCategory(selectableCategories[0].id);
    }
  }, [category, selectableCategories]);

  const handleFileChange = (e) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      setFile(selectedFile);
      
      // Create preview for images
      if (selectedFile.type.startsWith("image/")) {
        const reader = new FileReader();
        reader.onloadend = () => {
          setPreview(reader.result);
        };
        reader.readAsDataURL(selectedFile);
      } else {
        setPreview(null);
      }
      
      // Auto-fill name from filename if empty
      if (!name.trim()) {
        const fileName = selectedFile.name.replace(/\.[^/.]+$/, ""); // Remove extension
        setName(fileName);
      }
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!name.trim()) {
      alert("Please enter a template name");
      return;
    }
    
    onSave({
      name: name.trim(),
      category,
      imageUrl: preview, // Store the base64 preview
      fileUrl: preview,
    });
  };

  const handleAddCategory = () => {
    if (!onCategoryCreate) return;
    const trimmed = newCategoryName.trim();
    if (!trimmed) {
      alert("Please enter a category name");
      return;
    }

    const created = onCategoryCreate(trimmed);
    if (created) {
      setCategory(created.id);
      setNewCategoryName("");
      setManageCategories(true);
      setCategoryEdits((prev) => ({ ...prev, [created.id]: created.label }));
    }
  };

  const handleSaveCategory = (cat) => {
    if (!onCategoryUpdate) return;
    const nextLabel = (categoryEdits[cat.id] ?? cat.label).trim();
    if (!nextLabel) {
      alert("Category name cannot be empty");
      return;
    }

    if (nextLabel === cat.label) return;
    onCategoryUpdate(cat.id, nextLabel);
    setCategoryEdits((prev) => ({ ...prev, [cat.id]: nextLabel }));
  };

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center p-4"
      style={{
        background: "rgba(0,0,0,0.75)",
        backdropFilter: "blur(10px)",
        WebkitBackdropFilter: "blur(10px)",
      }}
      onClick={onClose}
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="w-full max-w-md rounded-2xl overflow-hidden mudo-glass-panel"
        style={{
          background: "rgba(18,18,18,0.95)",
          border: "1px solid rgba(255,255,255,0.1)",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-6 border-b border-white/10">
          <h2 className="text-xl font-bold">Add Template</h2>
          <p className="text-sm text-white/60 mt-1">
            Add a design from Canva or upload your own
          </p>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* File Upload */}
          <div>
            <label className="block text-sm font-medium mb-2">Upload Design</label>
            <div className="relative">
              <input
                type="file"
                accept="image/*,.pdf"
                onChange={handleFileChange}
                className="hidden"
                id="file-upload"
              />
              <label
                htmlFor="file-upload"
                className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-white/20 rounded-lg hover:border-purple-500 transition-colors cursor-pointer bg-white/5"
              >
                {preview ? (
                  <img src={preview} alt="Preview" className="h-full w-full object-contain rounded-lg" />
                ) : (
                  <>
                    <Plus className="w-8 h-8 mb-2 opacity-60" />
                    <span className="text-sm opacity-60">Click to upload or drag file</span>
                    <span className="text-xs opacity-40 mt-1">PNG, JPG, PDF</span>
                  </>
                )}
              </label>
            </div>
            {file && (
              <p className="text-xs text-green-400 mt-2">File selected: {file.name}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">Template Name</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g., Summer Workshop Poster"
              className="w-full px-4 py-2 rounded-lg bg-white/5 border border-white/10 focus:border-purple-500 focus:outline-none text-white"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">Category</label>
            <select
              value={category}
              onChange={(e) => {
                const v = e.target.value;
                if (v === "__add__") {
                  setManageCategories(true);
                  setTimeout(() => {
                    const el = document.getElementById("new-category-input");
                    el && el.focus();
                  }, 0);
                } else {
                  setCategory(v);
                }
              }}
              className="w-full px-4 py-2 rounded-lg bg-gray-900 border border-white/10 focus:border-purple-500 focus:outline-none text-white"
              style={{ color: "white", backgroundColor: "#111" }}
            >
              {selectableCategories.map((cat) => (
                <option key={cat.id} value={cat.id} style={{ color: "white", backgroundColor: "#111" }}>
                  {cat.label}
                </option>
              ))}
              <option value="__add__" style={{ color: "#a855f7", backgroundColor: "#111" }}>➕ Add new category…</option>
            </select>
            <button
              type="button"
              onClick={() => setManageCategories((prev) => !prev)}
              className="mt-2 text-xs text-purple-300 hover:text-purple-200 transition-colors"
            >
              {manageCategories ? "Hide category manager" : "Manage categories"}
            </button>
            {manageCategories && (
              <div className="mt-3 p-3 rounded-lg bg-white/5 border border-white/10 space-y-3" style={{ backdropFilter: "blur(12px)", WebkitBackdropFilter: "blur(12px)" }}>
                <div>
                  <label className="text-xs uppercase tracking-wide text-white/60">Add Category</label>
                  <div className="mt-1 flex gap-2">
                    <input
                      type="text"
                      value={newCategoryName}
                      onChange={(e) => setNewCategoryName(e.target.value)}
                      placeholder="e.g., Seasonal Campaigns"
                      className="flex-1 px-3 py-2 rounded-lg bg-black/30 border border-white/10 focus:border-purple-500 focus:outline-none text-sm"
                      id="new-category-input"
                    />
                    <button
                      type="button"
                      onClick={handleAddCategory}
                      className="px-3 py-2 rounded-lg text-sm font-semibold mudo-glass-interactive"
                      style={{ background: "var(--color-primary)" }}
                    >
                      Add
                    </button>
                  </div>
                </div>
                <div>
                  <label className="text-xs uppercase tracking-wide text-white/60">Rename Categories</label>
                  <div className="mt-1 space-y-2 max-h-40 overflow-y-auto pr-1">
                    {selectableCategories.map((cat) => {
                      const currentValue = categoryEdits[cat.id] ?? cat.label;
                      const isDirty = currentValue.trim() !== cat.label.trim();
                      return (
                        <div key={cat.id} className="flex gap-2 items-center">
                          <input
                            type="text"
                            value={currentValue}
                            onChange={(e) =>
                              setCategoryEdits((prev) => ({ ...prev, [cat.id]: e.target.value }))
                            }
                            className="flex-1 px-3 py-2 rounded-lg bg-black/30 border border-white/10 focus:border-purple-500 focus:outline-none text-sm"
                          />
                          <button
                            type="button"
                            disabled={!isDirty}
                            onClick={() => handleSaveCategory(cat)}
                            className="px-3 py-2 rounded-lg bg-white/10 hover:bg-white/20 disabled:opacity-40 disabled:cursor-not-allowed text-xs font-semibold"
                          >
                            Save
                          </button>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 rounded-lg bg-white/5 hover:bg-white/10 border border-white/10 mudo-glass-interactive"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="flex-1 px-4 py-2 rounded-lg font-semibold mudo-glass-interactive"
              style={{ 
                background: "var(--color-primary)",
                backdropFilter: "blur(16px)", 
                WebkitBackdropFilter: "blur(16px)",
                border: "1px solid rgba(255,255,255,0.2)"
              }}
            >
              Add Template
            </button>
          </div>
        </form>
      </motion.div>
    </div>
  );
}
